/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { getServiceDetails } from "./serviceAPI";
import { css } from "@emotion/react";
import { v4 } from "uuid";
import { useParams } from "react-router-dom";
import { PlayButton } from "../../shared/assets/icons/PlayButton.jsx";
import { CenteredTextWithUnderline } from "../../shared/components/reusableComp/CenteredTextWithUnderline";
import { yellow } from "@mui/material/colors";
import Markdown from "markdown-to-jsx";

const bannerWrapper = css`
  width: 100%;
  color: white;
  position: relative;
  img {
    width: 100%;
    vertical-align: bottom;
  }
`;
const cardWrapper = (direction, isImageRequiredOnRight, textMargin) => css`
  background-color: #121212;

  .MuiBox-root {
    display: flex;
    align-items: center;
    img {
      width: ${direction === "row" ? "50%" : "100%"};
      order: ${direction === "row" && isImageRequiredOnRight === true ? 2 : -1};
    }
    .MuiTypography-h5 {
      text-align: ${textMargin ? "center" : "unset"};
    }
    .MuiCardContent-root {
      padding: 0;
      margin: 20px 0;
      width: ${direction === "row" ? "50%" : "100%"};
      .MuiTypography-root {
        margin: ${direction === "row"
          ? isImageRequiredOnRight
            ? "30px 20px 30px 0px"
            : "30px 0px 30px 20px"
          : textMargin
          ? "10px 15px"
          : "10px 0px"};
      }
    }
  }
`;

const newsDetailWrapper = css`
  padding: 0 22%;

  .MuiTypography-h6 {
    color: white;
  }
`;
const descriptionWrapper = css`
  font-size: 14px;
  line-height: 35px;
`;

const mediaVideo = (matches) => css`
  width: ${matches ? "100%" : "auto"};
  margin: 0 ${matches ? "0" : "auto"};
  object-fit: unset;
  ${matches ? "max-height: 86vh;" : "min-height: 80vh; max-height: 88vh;"}
`;

const media = (matches) => css`
  ${matches ? "min-height: 25vh; max-height: 46vh;" : "max-height: 85vh;"}
`;

const renderCard = (
  imgUrl,
  imgName,
  title,
  description,
  direction,
  isImageRequiredOnRight,
  textMargin
) => {
  return (
    <Card
      css={() => cardWrapper(direction, isImageRequiredOnRight, textMargin)}
      key={v4()}
    >
      <Box sx={{ display: "flex", flexDirection: direction }}>
        {imgUrl && (
          <CardMedia
            component="img"
            image={`${process.env.REACT_APP_BASEURL}${imgUrl}`}
            alt={imgName}
          />
        )}
        <CardContent>
          <Typography
            gutterBottom
            variant="h5"
            component="div"
            color="text.secondary"
            style={{ fontSize: "24px" }}
          >
            {title}
          </Typography>
          <Typography
            css={descriptionWrapper}
            variant="body2"
            color="text.secondary"
          >
            <Markdown>{description}</Markdown>
          </Typography>
        </CardContent>
      </Box>
    </Card>
  );
};

const mediaContent = (banner, playVideo, setPlayVideo, matches) => {
  if (banner?.bannerVideoSource) {
    return (
      <div
        onClick={() => {
          setPlayVideo(true);
          document.getElementById("bannerVideoId").play();
        }}
      >
        <CardMedia
          id="bannerVideoId"
          component="video"
          src={banner?.bannerVideoSource}
          controls={!banner.showVideoPlayIcon || playVideo}
          muted
          css={() => mediaVideo(matches)}
          autoPlay={handleAutoPlay(banner.showVideoPlayIcon, playVideo)}
          loop
        />
        {!playVideo && banner.showVideoPlayIcon && (
          <PlayButton
            style={{
              fontSize: matches ? "50px" : "80px",
              position: "absolute",
              left: "44%",
              top: matches ? "45%" : "50%",
              cursor: "pointer",
            }}
            sx={{ color: yellow[50] }}
          ></PlayButton>
        )}
      </div>
    );
  } else {
    return (
      <a href={banner?.link ? banner?.link : "#"}>
        {(banner?.bannerImage?.data?.attributes?.formats?.small?.url ||
          banner?.bannerImage?.data?.attributes?.url) && (
          <CardMedia
            component="img"
            css={() => media(matches)}
            image={`${process.env.REACT_APP_BASEURL}${banner?.bannerImage?.data?.attributes?.url}`}
          ></CardMedia>
        )}
        {banner?.link && (
          <PlayButton
            style={{
              fontSize: matches ? "50px" : "80px",
              position: "absolute",
              left: "44%",
              top: matches ? "45%" : "50%",
              cursor: "pointer",
            }}
            sx={{ color: yellow[50] }}
          ></PlayButton>
        )}
      </a>
    );
  }
};

const getMobileView = (
  banner,
  newsDetailImgTextTileData,
  newsDetailCallToAction,
  newsDetailTextTiles,
  playVideo,
  setPlayVideo,
  matches
) => {
  return (
    <div>
      {
        <Box css={bannerWrapper}>
          {mediaContent(banner, playVideo, setPlayVideo, matches)}
        </Box>
      }
      <div style={{ padding: "0 8%" }}>
        <Box>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ marginTop: "8%", marginBottom: "4%" }}
          >
            <Box component="span">{newsDetailCallToAction?.Title}</Box>
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ paddingBottom: "6%" }}
            css={descriptionWrapper}
          >
            <Markdown>{newsDetailCallToAction?.description || ""}</Markdown>
          </Typography>
        </Box>
        {newsDetailCallToAction?.image?.data?.attributes?.url && (
          <CardMedia
            component="img"
            image={`${process.env.REACT_APP_BASEURL}${newsDetailCallToAction?.image?.data?.attributes?.url}`}
            alt={`${process.env.REACT_APP_BASEURL}${newsDetailCallToAction?.image?.data?.attributes?.name}`}
            sx={{ padding: "0% 0 8%" }}
          />
        )}
        <CenteredTextWithUnderline
          title={newsDetailTextTiles?.heading}
          description={newsDetailTextTiles?.description}
        />
        {newsDetailImgTextTileData?.map((dataItem) =>
          renderCard(
            dataItem?.image?.data?.attributes?.url,
            dataItem?.image?.data?.attributes?.name,
            dataItem?.title,
            dataItem?.description,
            "column"
          )
        )}
      </div>
    </div>
  );
};

const handleAutoPlay = (showPlayIcon, playVideo) => {
  if (showPlayIcon) {
    return playVideo;
  } else {
    return true;
  }
};

const getDesktopView = (
  banner,
  newsDetailImgTextTileData,
  newsDetailCallToAction,
  newsDetailTextTiles,

  playVideo,
  setPlayVideo,
  matches
) => {
  return (
    <div>
      {
        <Box css={bannerWrapper}>
          {mediaContent(banner, playVideo, setPlayVideo, matches)}
        </Box>
      }

      <div css={newsDetailWrapper}>
        <Box>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ marginTop: "6%" }}
          >
            <Box component="span">{newsDetailCallToAction?.Title}</Box>
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ paddingBottom: "4%" }}
            css={descriptionWrapper}
          >
            <Markdown>{newsDetailCallToAction?.description || ""}</Markdown>
          </Typography>
        </Box>
        {newsDetailCallToAction?.image?.data?.attributes?.url && (
          <CardMedia
            component="img"
            image={`${process.env.REACT_APP_BASEURL}${newsDetailCallToAction?.image?.data?.attributes?.url}`}
            alt={`${process.env.REACT_APP_BASEURL}${newsDetailCallToAction?.image?.data?.attributes?.name}`}
            sx={{ padding: "0% 0 6%" }}
          />
        )}

        <CenteredTextWithUnderline
          title={newsDetailTextTiles?.heading}
          description={newsDetailTextTiles?.description}
        />

        {newsDetailImgTextTileData?.map((dataItem) =>
          renderCard(
            dataItem?.image?.data?.attributes?.url,
            dataItem?.image?.data?.attributes?.name,
            dataItem?.title,
            dataItem?.description,
            "row",
            dataItem?.isImageRequiredOnRight
          )
        )}
      </div>
    </div>
  );
};

export default function ServiceGuide({ Slug }) {
  const [banner, setBanner] = useState([]);
  const [newsDetailImgTextTileData, setNewsDetailImgTextTileData] = useState(
    []
  );
  const [newsDetailCallToAction, setNewsDetailCallToAction] = useState([]);
  const [newsDetailTextTiles, setNewsDetailTextTiles] = useState([]);
  const [playVideo, setPlayVideo] = useState(false);

  const isMobile = useMediaQuery("(max-width: 480px)");

  const { slug } = useParams();

  useEffect(() => {
    getServiceDetails(slug || Slug).then((response) => {
      document.title = response?.data?.data?.attributes?.title;
      const data = response?.data?.data?.attributes?.body;
      setBanner(
        data?.filter(
          (i) => i.__component === "components.banner-image-with-text-tiles"
        )[0]
      );
      setNewsDetailImgTextTileData(
        data?.filter((i) => i.__component === "components.image-text-tiles")
      );
      setNewsDetailCallToAction(
        data?.filter((i) => i.__component === "shared.call-to-action")[0]
      );
      setNewsDetailTextTiles(
        data?.filter((i) => i.__component === "shared.text-tiles")[0]
      );
    });
  }, []);

  return (
    <div style={{ backgroundColor: "#121212" }}>
      {isMobile
        ? getMobileView(
            banner,
            newsDetailImgTextTileData,
            newsDetailCallToAction,
            newsDetailTextTiles,
            playVideo,
            setPlayVideo,
            isMobile
          )
        : getDesktopView(
            banner,
            newsDetailImgTextTileData,
            newsDetailCallToAction,
            newsDetailTextTiles,
            playVideo,
            setPlayVideo,
            isMobile
          )}
    </div>
  );
}
